/**
 * Shared validation utilities extracted from existing tools
 * to maintain consistency and prevent code duplication
 */

import { Organization, Team, Player, ValidationResult } from '../../types/shared'
import { PlayerValidationResult, RosterPlayer } from '../../types/movePlayerHelmet'
import API from '../../API/API'

/**
 * Result of organization selection with automatic team handling
 */
export interface OrgTeamSelectionResult {
  organization: Organization
  team: Team | null
  requiresTeamSelection: boolean
  displayName: string
  teams: Team[]
}

/**
 * Handles organization selection with automatic team selection logic
 * If org has only one team, auto-selects it and creates display name
 * If org has multiple teams, returns teams for user selection
 */
export const handleOrganizationSelection = async (
  selectedOrg: Organization
): Promise<OrgTeamSelectionResult> => {
  try {
    const response = await API.GetOrgTeams(selectedOrg.orgID)
    
    if (!response.success || !response.data || response.data.length === 0) {
      throw new Error(`No teams found for organization ${selectedOrg.orgName}`)
    }

    const teams = response.data
    
    if (teams.length === 1) {
      // Auto-select the single team and create combined display name
      const team = teams[0]
      const displayName = `${selectedOrg.orgName} - ${team.teamName}`
      
      return {
        organization: selectedOrg,
        team: team,
        requiresTeamSelection: false,
        displayName,
        teams
      }
    } else {
      // Multiple teams - user needs to select
      return {
        organization: selectedOrg,
        team: null,
        requiresTeamSelection: true,
        displayName: selectedOrg.orgName,
        teams
      }
    }
  } catch (error) {
    throw new Error(`Failed to load teams for organization: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Creates display name for organization/team combination
 */
export const createOrgTeamDisplayName = (org: Organization, team: Team): string => {
  return `${org.orgName} - ${team.teamName}`
}

/**
 * Validates if a required field has a value
 */
export const validateRequired = (value: any, fieldName: string): ValidationResult => {
  const valid = value !== null && value !== undefined && value !== ''
  return {
    valid,
    message: valid ? undefined : `${fieldName} is required`,
    field: fieldName.toLowerCase().replace(/\s+/g, '_')
  }
}

/**
 * Validates organization selection
 */
export const validateOrganization = (org: Organization | null): ValidationResult => {
  return validateRequired(org?.orgID, 'Organization')
}

/**
 * Validates team selection
 */
export const validateTeam = (team: Team | null): ValidationResult => {
  return validateRequired(team?.teamID, 'Team')
}

/**
 * Validates player selection
 */
export const validatePlayer = (player: Player | null): ValidationResult => {
  return validateRequired(player?.playerID, 'Player')
}

/**
 * Checks if a player name already exists on a target team
 */
export const checkDuplicatePlayerName = (
  player: RosterPlayer | any,
  targetTeamPlayers: RosterPlayer[] | any[]
): boolean => {
  if (!player || !Array.isArray(targetTeamPlayers)) return false
  
  // Handle different property name conventions
  const playerFirst = player.firstName || player.FirstName
  const playerLast = player.lastName || player.LastName
  
  if (!playerFirst || !playerLast) return false
  
  return targetTeamPlayers.some(
    existingPlayer => {
      const existingFirst = existingPlayer.firstName || existingPlayer.FirstName
      const existingLast = existingPlayer.lastName || existingPlayer.LastName
      
      return existingFirst?.toLowerCase() === playerFirst?.toLowerCase() &&
             existingLast?.toLowerCase() === playerLast?.toLowerCase() &&
             (existingPlayer.playerID || existingPlayer.PlayerID) !== (player.playerID || player.PlayerID)
    }
  )
}

/**
 * Comprehensive player transfer validation
 */
export const validatePlayerTransfer = (
  player: RosterPlayer | null,
  sourceTeam: Team | null,
  targetTeam: Team | null,
  targetTeamPlayers: RosterPlayer[] = []
): PlayerValidationResult => {
  const result: PlayerValidationResult = {
    valid: true,
    duplicateNameExists: false,
    targetTeamFull: false,
    playerEligible: true,
    errorMessages: []
  }

  // Check required selections
  if (!player) {
    result.valid = false
    result.playerEligible = false
    result.errorMessages.push('Player selection is required')
  }

  if (!sourceTeam) {
    result.valid = false
    result.errorMessages.push('Source team is required')
  }

  if (!targetTeam) {
    result.valid = false
    result.errorMessages.push('Target team is required')
  }

  // Check if moving to same team
  if (sourceTeam && targetTeam && sourceTeam.teamID === targetTeam.teamID) {
    result.valid = false
    result.errorMessages.push('Cannot move player to the same team')
  }

  // Check for duplicate names (per requirements)
  if (player && checkDuplicatePlayerName(player, targetTeamPlayers)) {
    result.valid = false
    result.duplicateNameExists = true
    result.errorMessages.push(
      `A player with the name "${player.firstName} ${player.lastName}" already exists on the target team`
    )
  }

  // Check player eligibility
  if (player && !player.isEligibleForTransfer) {
    result.valid = false
    result.playerEligible = false
    result.errorMessages.push('Selected player is not eligible for transfer')
  }

  return result
}

/**
 * Formats player name for display: "Last, First (#Jersey)"
 */
export const formatPlayerName = (player: Player): string => {
  const { firstName, lastName, jerseyNumber } = player
  
  if (!firstName || !lastName) {
    return 'Unknown Player'
  }

  const jersey = jerseyNumber ? ` (#${jerseyNumber})` : ''
  return `${lastName}, ${firstName}${jersey}`
}

/**
 * Checks if an organization has multiple teams
 */
export const hasMultipleTeams = (orgId: number, teams: Team[]): boolean => {
  return teams.filter(team => team.orgID === orgId).length > 1
}

/**
 * Gets teams for a specific organization
 */
export const getTeamsForOrganization = (orgId: number, teams: Team[]): Team[] => {
  return teams.filter(team => team.orgID === orgId)
}

/**
 * Filters organizations excluding a specific org (unless it has multiple teams)
 */
export const filterTargetOrganizations = (
  sourceOrgId: number,
  organizations: Organization[],
  teams: Team[]
): Organization[] => {
  return organizations.filter(org => {
    // Include org if it's not the source org
    if (org.orgID !== sourceOrgId) return true
    
    // Include source org only if it has multiple teams
    return hasMultipleTeams(sourceOrgId, teams)
  })
}

/**
 * Generic form validation helper
 */
export const validateForm = (values: Record<string, any>, requiredFields: string[]): Record<string, string> => {
  const errors: Record<string, string> = {}
  
  requiredFields.forEach(field => {
    const validation = validateRequired(values[field], field.replace(/([A-Z])/g, ' $1').trim())
    if (!validation.valid && validation.message) {
      errors[field] = validation.message
    }
  })
  
  return errors
}

/**
 * Alias for checkDuplicatePlayerName to match test expectations
 */
export const validateDuplicatePlayer = checkDuplicatePlayerName

/**
 * Validates complete form data for transfer submission
 */
export const validateFormCompleteness = (formData: {
  sourceOrgID?: string
  sourceTeamID?: string
  playerID?: string
  targetOrgID?: string
  targetTeamID?: string
  confirmTransfer?: boolean
}): boolean => {
  const required = ['sourceOrgID', 'sourceTeamID', 'playerID', 'targetOrgID', 'targetTeamID']
  
  // Check all required fields have values
  const hasAllFields = required.every(field => 
    formData[field as keyof typeof formData] && 
    formData[field as keyof typeof formData] !== ''
  )
  
  // Check confirmation is checked
  const isConfirmed = formData.confirmTransfer === true
  
  return hasAllFields && isConfirmed
}
